import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Destination } from "../../common/ObjectTypes";
import { buttonClasses, formClasses } from "../../common/tailwindClassnames";
import {
    FieldErrors,
    FieldMapping,
    Rule,
    StreamType,
    buildConfigWithRule,
} from "./fieldMappingTypes";
import FieldMappingForm from "./FieldMappingForm";
import FieldMappingCodeEditor from "./FieldMappingCodeEditor";
import RuleTypeSelector, { RuleType } from "./RuleTypeSelector";
import { useIsResizing } from "../../common/useIsResizing";

type Mode = "builder" | "code";

// The panel surfaces both client-side validation (required fields, etc.) and any
// server-side field errors returned from a 400. The shape is shared with the parent
// so it can populate this map from a parsed backend response.
type FormErrors = FieldErrors;

type TransformationRulesPanelProps = {
    isOpen: boolean;
    onClose: () => void;
    destination: Destination | null;
    providerId: string;
    currentRule: Rule | null;
    // Persisted route_configuration name, or "" when no configuration exists yet.
    currentConfigurationName: string;
    // onSave may resolve with field-level errors when the backend rejects the payload.
    // Returning void / undefined is treated as success.
    onSave: (streamType: StreamType, mapping: FieldMapping, name: string) => Promise<FieldErrors | void> | FieldErrors | void;
    onDelete: () => Promise<void> | void;
};

function deriveDefaultName(providerId: string, destinationField: string): string {
    return `${providerId} ${destinationField || ""}`.trim();
}

const emptyMapping: FieldMapping = {
    map: {},
    default: "",
    provider_field: "",
    destination_field: "",
};

const segmentBase = "px-3 py-1.5 text-sm font-medium rounded-sm cursor-pointer transition-colors duration-200";
const segmentActive = `${segmentBase} bg-ui-cta-primary text-white`;
const segmentInactive = `${segmentBase} bg-transparent text-ui-text-secondary-text hover:text-ui-text-primary-text`;

function buildJsonPreview(
    providerId: string,
    destinationId: string,
    streamType: StreamType,
    mapping: FieldMapping
): string {
    const config = buildConfigWithRule({}, providerId, destinationId, streamType, mapping);
    return JSON.stringify(config, null, 2);
}

const TransformationRulesPanel = ({
    isOpen,
    onClose,
    destination,
    providerId,
    currentRule,
    currentConfigurationName,
    onSave,
    onDelete,
}: TransformationRulesPanelProps) => {
    const { t } = useTranslation();
    // While resizing, drop the slide transition so the width switch at the md breakpoint
    // doesn't animate the closed (translate-x-full) panel across the viewport.
    const isResizing = useIsResizing();
    const [showForm, setShowForm] = useState(false);
    // When true, the rule-type selector ("Select rule type" → Field Mapping) is shown before the form.
    const [selectingType, setSelectingType] = useState(false);
    const [showNewRuleTooltip, setShowNewRuleTooltip] = useState(false);
    const [selectedRuleType, setSelectedRuleType] = useState<RuleType>("field_mapping");
    const [streamType, setStreamType] = useState<StreamType>("ev");
    const [mapping, setMapping] = useState<FieldMapping>(emptyMapping);
    const [mode, setMode] = useState<Mode>("builder");
    const [code, setCode] = useState<string>("");
    const [codeValid, setCodeValid] = useState<Rule | null>(null);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState<FormErrors>({});
    // Increments whenever the panel (re)opens for a rule, so the form / code editor can
    // be keyed off it and unmount any local UI state (like the "Add value mapping"
    // checkbox) that would otherwise leak between destinations.
    const [resetTick, setResetTick] = useState(0);
    // Editable name for the route_configuration. Defaults to "<providerId> <destination_field>"
    // and auto-syncs as the user fills the destination_field, unless they manually edit it.
    const [configName, setConfigName] = useState("");
    const [nameWasEdited, setNameWasEdited] = useState(false);
    // Captured when the form opens; Save stays disabled until something changes against it
    // so a no-op click can't trigger a duplicate route_configuration on the backend.
    const [snapshot, setSnapshot] = useState<{
        streamType: StreamType;
        mapping: string;
        configName: string;
    } | null>(null);

    const destinationId = destination?.id ?? "";

    useEffect(() => {
        if (!isOpen) return;
        // When a rule already exists we land on the list view (not the form) so the user
        // can see what's configured and choose to edit or delete it. The form fields are
        // still primed from the rule so clicking Edit shows the saved values immediately.
        setShowForm(false);
        setSelectingType(false);
        setSelectedRuleType("field_mapping");
        setShowNewRuleTooltip(false);
        if (currentRule) {
            setStreamType(currentRule.streamType);
            setMapping(currentRule.mapping);
            if (currentConfigurationName) {
                // A saved name is treated as manually-set so we don't overwrite it when the
                // user edits destination_field afterwards.
                setConfigName(currentConfigurationName);
                setNameWasEdited(true);
            } else {
                // Legacy config without a name — fall back to the derived default.
                setConfigName(deriveDefaultName(providerId, currentRule.mapping.destination_field ?? ""));
                setNameWasEdited(false);
            }
        } else {
            setStreamType("ev");
            setMapping(emptyMapping);
            setConfigName(deriveDefaultName(providerId, ""));
            setNameWasEdited(false);
        }
        setMode("builder");
        setErrors({});
        // Clear stale validation from a previously-edited rule so Save isn't briefly
        // enabled on a different destination before the editor re-validates.
        setCodeValid(null);
        setResetTick((tick) => tick + 1);
        setSnapshot(null);
    }, [isOpen, currentRule, currentConfigurationName, destinationId, providerId]);

    // Snapshot the values that determine "is this dirty?" right as we enter the form. We
    // compare against this snapshot (and not currentRule directly) so it stays stable
    // regardless of how the user navigated into the form.
    useEffect(() => {
        if (!showForm) {
            setSnapshot(null);
            return;
        }
        setSnapshot({
            streamType,
            mapping: JSON.stringify(mapping),
            configName,
        });
        // We intentionally snapshot only when the form opens — once inside, edits should
        // mark the form as dirty rather than re-baselining.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showForm]);

    useEffect(() => {
        if (mode === "code" && destinationId && providerId) {
            setCode(buildJsonPreview(providerId, destinationId, streamType, mapping));
        }
    }, [mode, providerId, destinationId, streamType, mapping]);

    const builderValid = useMemo(() => {
        if ((mapping.destination_field ?? "").trim() === "") return false;
        // provider_field is only required when value mappings are populated — applies to
        // both Events and Observations now that value mapping is enabled for both.
        const mapHasEntries = !!mapping.map && Object.keys(mapping.map).length > 0;
        if (mapHasEntries) {
            return (mapping.provider_field ?? "").trim() !== "";
        }
        return true;
    }, [mapping.destination_field, mapping.provider_field, mapping.map]);

    const nameIsValid = configName.trim() !== "";

    // The form is "dirty" when the rule (per the active mode) or the name differ from the
    // snapshot taken at form-open. Save stays disabled when nothing changed so we don't
    // ping the backend (and create a duplicate route_configuration) on a no-op click.
    const isDirty = useMemo(() => {
        if (!snapshot) return false;
        if (configName !== snapshot.configName) return true;
        if (mode === "builder") {
            if (streamType !== snapshot.streamType) return true;
            if (JSON.stringify(mapping) !== snapshot.mapping) return true;
            return false;
        }
        if (!codeValid) return false;
        if (codeValid.streamType !== snapshot.streamType) return true;
        return JSON.stringify(codeValid.mapping) !== snapshot.mapping;
    }, [snapshot, mode, streamType, mapping, codeValid, configName]);

    const ruleIsValid = mode === "builder" ? builderValid : codeValid !== null;
    const canSave = ruleIsValid && nameIsValid && isDirty;

    // Note on form state: FieldMappingForm/FieldMappingCodeEditor are keyed by `resetTick`
    // and only mount while `showForm` is true. Every path that leaves the form (handleCancel,
    // a successful save → open effect re-run, panel reopen) bumps `resetTick` and clears
    // `codeValid`, so the next time the form is shown it remounts fresh. handleSelectRuleType
    // and handleEdit therefore don't need to bump the tick themselves — the prior exit did.

    // "New Rule" first shows the rule-type selector; the form opens only after a type is chosen.
    const handleStartNew = () => {
        setSelectedRuleType("field_mapping");
        setSelectingType(true);
    };

    const handleSelectRuleType = (type: RuleType) => {
        setSelectedRuleType(type);
        // Only Field Mapping exists today; future rule types would branch on `type` here.
        setStreamType("ev");
        setMapping(emptyMapping);
        setMode("builder");
        setErrors({});
        setSelectingType(false);
        setShowForm(true);
    };

    const handleEdit = () => {
        if (!currentRule) return;
        setStreamType(currentRule.streamType);
        setMapping(currentRule.mapping);
        setMode("builder");
        setErrors({});
        setShowForm(true);
    };

    const handleCancel = () => {
        // Editing an existing rule returns to the list; creating a new one closes the panel.
        if (currentRule) {
            setStreamType(currentRule.streamType);
            setMapping(currentRule.mapping);
            setMode("builder");
            setErrors({});
            setCodeValid(null);
            setResetTick((tick) => tick + 1);
            setShowForm(false);
        } else {
            onClose();
        }
    };

    // Cancel from the rule-type selector backs out to the empty state (no rule yet).
    const handleCancelSelectType = () => {
        setSelectingType(false);
    };

    const handleSwitchMode = (next: Mode) => {
        // Switching code → builder: if the JSON is valid, carry its values into the builder
        // so the user doesn't silently lose what they typed in code mode.
        if (next === "builder" && mode === "code" && codeValid) {
            setStreamType(codeValid.streamType);
            setMapping(codeValid.mapping);
        }
        setMode(next);
    };

    const handleSave = async () => {
        if (saving) return;
        const trimmedName = configName.trim();
        if (!trimmedName) return;
        const runSave = async (
            saveStreamType: StreamType,
            saveMapping: FieldMapping
        ): Promise<void> => {
            setSaving(true);
            try {
                const serverErrors = await onSave(saveStreamType, saveMapping, trimmedName);
                // Backend rejected the payload — show its field errors on top of the form
                // so the user can correct them. We intentionally don't clear other errors:
                // setErrors is a full replacement so it stays in sync with the latest save.
                if (serverErrors && Object.keys(serverErrors).length > 0) {
                    setErrors(serverErrors);
                } else {
                    setErrors({});
                }
            } finally {
                setSaving(false);
            }
        };
        if (mode === "builder") {
            const nextErrors: FormErrors = {};
            if ((mapping.destination_field ?? "").trim() === "") nextErrors.destination_field = t("Field to change");
            const mapHasEntries = !!mapping.map && Object.keys(mapping.map).length > 0;
            if (mapHasEntries && (mapping.provider_field ?? "").trim() === "") {
                nextErrors.provider_field = t("Source field for mapping");
            }
            setErrors(nextErrors);
            if (Object.keys(nextErrors).length > 0) return;
            await runSave(streamType, mapping);
        } else if (codeValid) {
            await runSave(codeValid.streamType, codeValid.mapping);
        }
    };

    const handleDelete = async () => {
        if (saving) return;
        setSaving(true);
        try {
            await onDelete();
        } finally {
            setSaving(false);
        }
    };

    // Below the md breakpoint (the top-nav hamburger threshold) the panel goes full-screen.
    // It sits below the mobile nav menu (z-59/60) but the floating "?" and Help panel stack
    // above it (z-57 / z-58) so help is reachable over the full-screen Rules panel.
    return (
        <div
            data-testid="TransformationRulesPanel-panel"
            className={`fixed top-0 right-0 h-full w-full md:w-[520px] bg-white shadow-xl z-[56] md:z-[60] transition-transform duration-300 flex flex-col ${isOpen ? "translate-x-0" : "translate-x-full"
                }`}
            style={isResizing ? { transition: "none" } : undefined}
            aria-hidden={!isOpen}
            {...(!isOpen && { inert: "" } as unknown as React.HTMLAttributes<HTMLDivElement>)}
        >
            <div className="flex items-center justify-between px-4 py-4 border-b border-unlisted-gray-lines">
                <div className="flex flex-col">
                    <h2 className="text-lg font-semibold text-ui-text-primary-text">{t("Rules")}</h2>
                    {destination && (
                        <span className="text-sm text-ui-text-primary-text truncate">{destination.name}</span>
                    )}
                </div>
                <button
                    onClick={onClose}
                    className="w-9 h-9 flex items-center justify-center text-ui-text-secondary-text hover:text-ui-text-primary-text"
                    aria-label={t("Close")}
                    data-testid="TransformationRulesPanel-close"
                >
                    ✕
                </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4">
                {!showForm && !selectingType && currentRule ? (
                    <div className="flex flex-col gap-3" data-testid="TransformationRulesPanel-list">
                        <div
                            className="bg-unlisted-very-light-gray border border-unlisted-gray-lines rounded p-4 flex flex-row items-start justify-between gap-3"
                            data-testid="TransformationRulesPanel-rule-item"
                        >
                            <div className="flex flex-col gap-1 min-w-0">
                                <span className="text-xs font-medium uppercase tracking-wide text-ui-text-primary-text">
                                    {currentRule.streamType === "ev" ? t("Events") : t("Observations")}
                                </span>
                                <span className="text-sm font-semibold text-ui-text-primary-text break-all">
                                    {currentRule.mapping.destination_field || t("Field Mapping")}
                                </span>
                                {currentRule.mapping.default ? (
                                    <span className="text-xs text-ui-text-primary-text break-all">
                                        {`${t("Default value")}: ${currentRule.mapping.default}`}
                                    </span>
                                ) : null}
                            </div>
                            <div className="flex flex-row items-center gap-3 shrink-0">
                                <button
                                    type="button"
                                    onClick={handleEdit}
                                    className="text-ui-cta-primary hover:underline font-semibold text-sm"
                                    data-testid="TransformationRulesPanel-edit"
                                >
                                    {t("Edit")}
                                </button>
                                <button
                                    type="button"
                                    onClick={handleDelete}
                                    disabled={saving}
                                    className="text-unlisted-orange hover:text-red-2 font-semibold text-sm transition-colors duration-300 disabled:opacity-50"
                                    data-testid="TransformationRulesPanel-delete"
                                >
                                    {t("Delete")}
                                </button>
                            </div>
                        </div>
                        {/* Disabled because only one field-mapping rule is supported today. The
                            tooltip lives on a wrapper so it still shows on hover — a disabled
                            button doesn't fire its own mouse events. */}
                        <div
                            className="relative inline-block self-start"
                            onMouseEnter={() => setShowNewRuleTooltip(true)}
                            onMouseLeave={() => setShowNewRuleTooltip(false)}
                        >
                            <button
                                type="button"
                                disabled
                                aria-describedby="new-rule-tooltip"
                                className={buttonClasses.primarySm}
                                data-testid="TransformationRulesPanel-new-rule"
                            >
                                {t("New Rule")}
                            </button>
                            {showNewRuleTooltip && (
                                <div
                                    id="new-rule-tooltip"
                                    role="tooltip"
                                    className="w-56 absolute bottom-full left-0 mb-2 bg-white border border-unlisted-gray-lines p-2 rounded shadow-lg z-10 text-sm text-ui-text-primary-text"
                                    data-testid="TransformationRulesPanel-new-rule-tooltip"
                                >
                                    {t("Only one field mapping rule is supported for now.")}
                                </div>
                            )}
                        </div>
                    </div>
                ) : !showForm && selectingType ? (
                    <div className="flex flex-col gap-3" data-testid="TransformationRulesPanel-select-type">
                        <div
                            className="bg-unlisted-light-gray border border-dashed border-unlisted-gray-lines rounded px-3 flex items-center"
                            style={{ minHeight: 80 }}
                        >
                            <span className="text-ui-text-primary-text text-sm">{t("No rule configured yet")}</span>
                        </div>
                        <RuleTypeSelector value={selectedRuleType} onSelect={handleSelectRuleType} />
                    </div>
                ) : !showForm ? (
                    <div className="flex flex-col gap-3" data-testid="TransformationRulesPanel-empty">
                        <div
                            className="bg-unlisted-light-gray border border-dashed border-unlisted-gray-lines rounded px-3 flex items-center"
                            style={{ minHeight: 80 }}
                        >
                            <span className="text-ui-text-primary-text text-sm">{t("No rule configured yet")}</span>
                        </div>
                        <button
                            type="button"
                            onClick={handleStartNew}
                            className={`${buttonClasses.primarySm} self-start`}
                            data-testid="TransformationRulesPanel-new-rule"
                        >
                            {t("New Rule")}
                        </button>
                    </div>
                ) : (
                    <div className="flex flex-col gap-4">
                    <div className="bg-unlisted-very-light-gray border border-unlisted-gray-lines rounded p-4">
                        <h3 className="text-base font-semibold text-ui-text-primary-text mb-3">{t("Route Configuration")}</h3>
                        <label className={formClasses.label} htmlFor="route-config-name-input">
                            {t("Name")}
                        </label>
                        <input
                            id="route-config-name-input"
                            data-testid="TransformationRulesPanel-config-name"
                            className={formClasses.input}
                            type="text"
                            value={configName}
                            onChange={(event) => {
                                setConfigName(event.target.value);
                                setNameWasEdited(true);
                            }}
                        />
                    </div>
                    <div className="bg-unlisted-very-light-gray border border-unlisted-gray-lines rounded">
                        <div className="flex flex-row justify-between items-center p-4">
                            <h3 className="text-base font-semibold text-ui-text-primary-text">{t("Field Mapping")}</h3>
                            <div
                                role="tablist"
                                aria-label={t("Field Mapping")}
                                className="inline-flex bg-unlisted-light-gray rounded p-0.5"
                            >
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={mode === "builder"}
                                    onClick={() => handleSwitchMode("builder")}
                                    className={mode === "builder" ? segmentActive : segmentInactive}
                                    data-testid="TransformationRulesPanel-mode-builder"
                                >
                                    {t("Builder")}
                                </button>
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={mode === "code"}
                                    onClick={() => handleSwitchMode("code")}
                                    className={mode === "code" ? segmentActive : segmentInactive}
                                    data-testid="TransformationRulesPanel-mode-code"
                                >
                                    {t("Code")}
                                </button>
                            </div>
                        </div>

                        {mode === "builder" ? (
                            <FieldMappingForm
                                key={resetTick}
                                streamType={streamType}
                                mapping={mapping}
                                errors={errors}
                                onChange={({ streamType: nextStream, mapping: nextMapping }) => {
                                    setStreamType(nextStream);
                                    setMapping(nextMapping);
                                    // Auto-fill the configuration name from destination_field while the
                                    // user hasn't manually edited it.
                                    if (!nameWasEdited) {
                                        setConfigName(deriveDefaultName(providerId, nextMapping.destination_field ?? ""));
                                    }
                                }}
                            />
                        ) : (
                            <FieldMappingCodeEditor
                                key={resetTick}
                                value={code}
                                onChange={setCode}
                                providerId={providerId}
                                destinationId={destinationId}
                                onValidChange={setCodeValid}
                            />
                        )}
                    </div>
                    </div>
                )}
            </div>

            {selectingType && (
                <div className="px-4 py-3 border-t border-unlisted-gray-lines flex flex-row justify-end gap-3">
                    <button
                        type="button"
                        onClick={handleCancelSelectType}
                        className={buttonClasses.cancelSm}
                        data-testid="TransformationRulesPanel-select-type-cancel"
                    >
                        {t("Cancel")}
                    </button>
                    <button
                        type="button"
                        onClick={() => handleSelectRuleType(selectedRuleType)}
                        className={buttonClasses.primarySm}
                        data-testid="TransformationRulesPanel-select-type-next"
                    >
                        {t("Next")}
                    </button>
                </div>
            )}

            {showForm && (
                <div className="px-4 py-3 border-t border-unlisted-gray-lines flex flex-row justify-between gap-2">
                    <div>
                        {currentRule && (
                            <button
                                type="button"
                                onClick={handleDelete}
                                disabled={saving}
                                className="text-unlisted-orange hover:text-red-2 font-semibold transition-colors duration-300 disabled:opacity-50 py-2"
                                data-testid="TransformationRulesPanel-delete-footer"
                            >
                                {t("Delete Rule")}
                            </button>
                        )}
                    </div>
                    <div className="flex flex-row gap-3">
                        <button
                            type="button"
                            onClick={handleCancel}
                            className={buttonClasses.cancelSm}
                            data-testid="TransformationRulesPanel-cancel"
                        >
                            {t("Cancel")}
                        </button>
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={!canSave || saving}
                            className={buttonClasses.primarySm}
                            data-testid="TransformationRulesPanel-save"
                        >
                            {t("Save Rule")}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TransformationRulesPanel;
